/**
 * Live Azure Speech usage reporter (ai_call_log, migration 0091).
 *
 * The interview's recogniser and Asha's voice talk to Azure directly from this page, and Azure's
 * own metrics cannot be split per interview — so this page is the only place the per-interview
 * quantity exists. It measures two things and nothing else:
 *
 *   STT — milliseconds of microphone audio streamed per recogniser run (Azure bills real-time STT
 *         on audio SENT, per second — silence included), from the moment the run starts capturing
 *         to the moment it is closed, and only for runs whose Azure session actually started;
 *   TTS — billable characters of each synthesis request Azure accepted (ttsBillableCharacters).
 *
 * No audio, transcript or spoken text leaves the page through here — only a duration or a count.
 * The server decides whose session it is, bounds and prices it (apps/api/src/utils/speechUsage.ts).
 *
 * DELIVERY. Reports queue in memory and flush every FLUSH_MS, and via sendBeacon when the page is
 * hidden or unloaded. Every report carries its own uuid, which the server stores as the row's
 * call_id — so a report sent twice (a beacon, then a normal flush if the page came back) lands
 * once. Accounting must never disturb the interview: every path here swallows its own errors.
 */
import { useEffect, useMemo } from "react";

export interface SpeechUsageSink {
  /** One recogniser run: milliseconds of microphone audio streamed to Azure. */
  stt(audioMs: number): void;
  /** One synthesis request Azure accepted: its billable characters. */
  tts(characters: number): void;
}

interface Report {
  id: string;
  kind: "stt" | "tts";
  audioMs?: number;
  characters?: number;
}

export const FLUSH_MS = 20_000;
/** Server-side batch ceiling (speechUsageBodySchema). */
const MAX_BATCH = 100;

/** A v4 uuid, or null where the platform cannot make one (the report is then simply not sent). */
// No default parameter: an explicit `undefined` ("this browser has no crypto") must mean exactly
// that, not silently fall back to globalThis.crypto.
export function newReportId(
  c: Pick<Crypto, "getRandomValues"> & Partial<Pick<Crypto, "randomUUID">> | undefined,
): string | null {
  try {
    if (!c) return null;
    if (typeof c.randomUUID === "function") return c.randomUUID();
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6]! & 0x0f) | 0x40;
    b[8] = (b[8]! & 0x3f) | 0x80;
    const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  } catch {
    return null;
  }
}

export interface ReporterTransport {
  /** Normal flush. Resolves true when the server accepted the batch. */
  post(url: string, body: string): Promise<boolean>;
  /** Page going away: fire-and-forget. */
  beacon(url: string, body: string): boolean;
}

const browserTransport: ReporterTransport = {
  async post(url, body) {
    // text/plain keeps this a CORS "simple" request — the same shape the beacon sends.
    const res = await fetch(url, { method: "POST", body, headers: { "content-type": "text/plain" }, keepalive: true });
    return res.ok;
  },
  beacon(url, body) {
    return typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
      ? navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }))
      : false;
  },
};

/** The queue + transport, framework-free so it can be tested directly. */
export function createSpeechUsageReporter(
  url: string,
  transport: ReporterTransport = browserTransport,
  mintId: () => string | null = () => newReportId(globalThis.crypto),
) {
  const queue: Report[] = [];
  let inFlight = false;

  const push = (r: Omit<Report, "id">): void => {
    const id = mintId();
    if (!id) return;
    queue.push({ id, ...r });
  };

  const sink: SpeechUsageSink = {
    stt(audioMs) {
      const ms = Math.round(audioMs);
      if (Number.isFinite(ms) && ms > 0) push({ kind: "stt", audioMs: ms });
    },
    tts(characters) {
      const n = Math.floor(characters);
      if (Number.isFinite(n) && n > 0) push({ kind: "tts", characters: n });
    },
  };

  /** Send what is queued; drop it only once the server has accepted it. */
  async function flush(): Promise<void> {
    if (inFlight || queue.length === 0) return;
    inFlight = true;
    const batch = queue.slice(0, MAX_BATCH);
    try {
      if (await transport.post(url, JSON.stringify({ reports: batch }))) queue.splice(0, batch.length);
    } catch {
      /* stays queued — the next flush (or the beacon) carries it */
    } finally {
      inFlight = false;
    }
  }

  /** Page hidden/unloading: beacon everything queued. Kept queued: ids make a resend harmless. */
  function beacon(): void {
    try {
      for (let i = 0; i < queue.length; i += MAX_BATCH) {
        transport.beacon(url, JSON.stringify({ reports: queue.slice(i, i + MAX_BATCH) }));
      }
    } catch {
      /* nothing to do — the page is going away */
    }
  }

  return { sink, flush, beacon, pending: (): number => queue.length };
}

/**
 * One reporter for the interview page while Azure speech is on. Returns null otherwise, so the
 * Web Speech fallback (free) is never reported as Azure usage.
 */
export function useSpeechUsageReporter(inviteToken: string, baseUrl: string, enabled: boolean): SpeechUsageSink | null {
  const reporter = useMemo(
    () =>
      enabled && inviteToken
        ? createSpeechUsageReporter(`${baseUrl}/invite/${inviteToken}/interview/speech-usage`)
        : null,
    [enabled, inviteToken, baseUrl],
  );
  useEffect(() => {
    if (!reporter) return undefined;
    const timer = window.setInterval(() => void reporter.flush(), FLUSH_MS);
    const onHide = (): void => {
      if (document.visibilityState === "hidden") reporter.beacon();
    };
    const onPageHide = (): void => reporter.beacon();
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
      // Interview step unmounting (completed). Deferred one task: the recogniser's own cleanup —
      // which reports its final run — may run AFTER this one in the same commit.
      window.setTimeout(() => void reporter.flush(), 0);
    };
  }, [reporter]);
  return reporter?.sink ?? null;
}
